import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DensityName, ThemeName } from '@/theme';

// UI preferences that live outside the server state graph: theme, density,
// active persona (drives Home layout + nav visibility), and chrome layout
// toggles like the right-side tweaks panel.

export type PersonaId =
  | 'admin'
  | 'owner'
  | 'developer'
  | 'sponsor'
  | 'devops'
  | 'raqa'
  | 'compliance'
  | 'ai-council';

export interface PersonaMeta {
  id: PersonaId;
  label: string;
  // Gatekeeper personas can review phase gates on projects.
  gatekeeper?: boolean;
  // Plain-English description shown in the role picker.
  hint?: string;
}

export const PERSONAS: PersonaMeta[] = [
  {
    id: 'admin',
    label: 'Admin',
    hint: 'See the portfolio across all projects, plus user and system administration.',
  },
  {
    id: 'owner',
    label: 'Owner',
    hint: 'Focus on projects you own — drive them through their phase gates.',
  },
  {
    id: 'developer',
    label: 'Developer',
    hint: 'Tickets assigned to you come first, plus projects you contribute to.',
  },
  {
    id: 'sponsor',
    label: 'Sponsor',
    gatekeeper: true,
    hint: 'Projects you sponsor are the focus. Also gates Go-Live approval.',
  },
  {
    id: 'devops',
    label: 'DevOps',
    gatekeeper: true,
    hint: 'Reviews IT Review gates. Surface deployment and infrastructure work.',
  },
  {
    id: 'raqa',
    label: 'RA/QA',
    gatekeeper: true,
    hint: 'Regulatory + Quality Assurance review of validated projects.',
  },
  {
    id: 'compliance',
    label: 'Compliance',
    gatekeeper: true,
    hint: 'Reviews HIPAA/PII and Security gates on PHI-handling projects.',
  },
  {
    id: 'ai-council',
    label: 'AI Council',
    gatekeeper: true,
    hint: 'Cross-cutting AI governance review. Lands on Design and Active gates.',
  },
];

export const GATEKEEPER_PERSONAS = new Set<PersonaId>(
  PERSONAS.filter((p) => p.gatekeeper).map((p) => p.id),
);

// Migrate persona values persisted under the older taxonomy.
const PERSONA_MIGRATIONS: Record<string, PersonaId> = {
  approver: 'sponsor',
  manager: 'owner',
  helpdesk: 'owner',
  exec: 'owner',
  member: 'developer',
};

function normalizePersona(value: unknown): PersonaId {
  if (typeof value !== 'string') return 'owner';
  if (PERSONAS.some((p) => p.id === value)) return value as PersonaId;
  if (value in PERSONA_MIGRATIONS) return PERSONA_MIGRATIONS[value]!;
  return 'owner';
}

interface UiPrefsState {
  theme: ThemeName;
  density: DensityName;
  persona: PersonaId;
  tweaksOpen: boolean;
  // Left-side search chat panel (Phase 5 will fill it with an agent).
  searchChatOpen: boolean;
  searchChatSeed: string;
  // Right-side Atlas chat panel (intake / general assistant).
  chatOpen: boolean;
  setTheme: (t: ThemeName) => void;
  setDensity: (d: DensityName) => void;
  setPersona: (p: PersonaId) => void;
  setTweaksOpen: (open: boolean) => void;
  openSearchChat: (seed?: string) => void;
  closeSearchChat: () => void;
  setChatOpen: (open: boolean) => void;
}

export const useUiPrefs = create<UiPrefsState>()(
  persist(
    (set) => ({
      theme: 'cream',
      density: 'regular',
      persona: 'owner',
      tweaksOpen: false,
      searchChatOpen: false,
      searchChatSeed: '',
      chatOpen: false,
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setPersona: (persona) => set({ persona }),
      setTweaksOpen: (tweaksOpen) => set({ tweaksOpen }),
      openSearchChat: (seed = '') => set({ searchChatOpen: true, searchChatSeed: seed }),
      closeSearchChat: () => set({ searchChatOpen: false }),
      setChatOpen: (chatOpen) => set({ chatOpen }),
    }),
    {
      name: 'atlas-ui-prefs',
      version: 2,
      migrate: (state) => {
        if (!state || typeof state !== 'object') return state as UiPrefsState;
        const s = state as Partial<UiPrefsState> & Record<string, unknown>;
        return {
          ...(s as UiPrefsState),
          persona: normalizePersona(s.persona),
        };
      },
    },
  ),
);

if (typeof document !== 'undefined') {
  const apply = (state: UiPrefsState) => {
    document.documentElement.dataset.theme = state.theme;
    document.documentElement.dataset.density = state.density;
  };
  apply(useUiPrefs.getState());
  useUiPrefs.subscribe(apply);
}
